import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, Eye, EyeOff, LogIn, AlertCircle, Mail, ShieldCheck, ShieldAlert } from "lucide-react";
import { authApi, pharmacienAuthApi } from "../../services/api";
import logoHorizontale from "../../assets/logo/logo-horizontale.svg";

const AdminLogin = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [autoLoggedOut, setAutoLoggedOut] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (sessionStorage.getItem("autoLogout")) {
      setAutoLoggedOut(true);
      sessionStorage.removeItem("autoLogout");
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);
    try {
      // Essai 1 : login admin (table users)
      let token, role, userName, userEmail, userId;
      try {
        const data = await authApi.login(email, password);
        token     = data.accessToken;
        role      = data.role?.toLowerCase() || "";
        userName  = data.nomComplet || data.email || email;
        userEmail = data.email || email;
        userId    = data.userId || "";
        if (data.refreshToken) localStorage.setItem("refreshToken", data.refreshToken);
      } catch {
        // Essai 2 : login pharmacien (table pharmaciens)
        const data = await pharmacienAuthApi.login(email, password);
        token     = data.token;
        role      = "pharmacien";
        userName  = data.pharmacien
          ? `${data.pharmacien.prenom || ""} ${data.pharmacien.nom || ""}`.trim()
          : email;
        userEmail = data.pharmacien?.email || email;
        userId    = data.pharmacien?.id || "";
      }

      localStorage.setItem("token", token);
      localStorage.setItem("role", role);
      localStorage.setItem("userName", userName);
      localStorage.setItem("userEmail", userEmail);
      localStorage.setItem("userId", userId);

      if (role.includes("admin") || role.includes("super_admin")) {
        navigate("/super-admin");
      } else {
        navigate("/pharmacist");
      }
    } catch (err) {
      setError(err.message || "Identifiants incorrects.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-950 flex items-center justify-center p-4 font-[Inter,sans-serif]">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex bg-white rounded-2xl px-4 py-2 mb-6 shadow-lg">
            <img src={logoHorizontale} alt="SunuFarmasi" className="h-20 w-auto" />
          </div>
          {/* <div className="inline-flex items-center justify-center w-12 h-12 bg-emerald-600/20 border border-emerald-500/30 rounded-2xl mb-3">
            <ShieldCheck size={24} className="text-emerald-400" />
          </div> */}
          <h1 className="text-2xl font-bold text-white mb-1">Accès Administration</h1>
          <p className="text-gray-500 text-sm">Réservé aux administrateurs SunuFarmasi</p>
        </div>

        {/* Card */}
        <div className="bg-gray-900 border border-gray-800 rounded-2xl p-8">
          {autoLoggedOut && (
            <div className="mb-5 p-3.5 bg-amber-950/40 border border-amber-700/50 rounded-xl flex items-start space-x-3">
              <ShieldAlert className="text-amber-400 flex-shrink-0 mt-0.5" size={17} />
              <p className="text-amber-300 text-sm">
                Pour des raisons de sécurité, vous avez été déconnecté automatiquement après 5 minutes d'inactivité.
              </p>
            </div>
          )}

          {error && (
            <div className="mb-5 p-3.5 bg-red-950/50 border border-red-800/50 rounded-xl flex items-start space-x-3">
              <AlertCircle className="text-red-400 flex-shrink-0 mt-0.5" size={17} />
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Email</label>
              <div className="relative">
                <Mail size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-500" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@sunufarmasi.sn"
                  required
                  className="w-full pl-10 pr-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-sm text-white placeholder:text-gray-600 focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 transition-all"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Mot de passe</label>
              <div className="relative">
                <Lock size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-500" />
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="w-full pl-10 pr-11 py-3 bg-gray-800 border border-gray-700 rounded-xl text-sm text-white placeholder:text-gray-600 focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 transition-colors"
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3.5 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl font-semibold text-sm transition-all disabled:opacity-50 flex items-center justify-center space-x-2 mt-2"
            >
              {isLoading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Connexion...</span>
                </>
              ) : (
                <>
                  <LogIn size={16} />
                  <span>Se connecter</span>
                </>
              )}
            </button>
          </form>
        </div>

        <p className="text-center text-xs text-gray-700 mt-6">
          © 2026 SunuFarmasi · Accès restreint
        </p>
      </div>
    </div>
  );
};

export default AdminLogin;
